/**
 * areaIdentityStartController — testable orchestration of the "start identity
 * verification" click (PR-ONB-PREAUTH-4, review FIX 1).
 *
 * Two responsibilities the component must NOT get wrong:
 *   1. on a REDIRECT outcome the busy flag must STAY set — the browser is about
 *      to navigate away, so re-enabling the button (a `finally { setBusy(false) }`
 *      runs even after `return`) would open a re-click window;
 *   2. a second click while a start is in flight must NOT fire a second start.
 *
 * The busy flag is owned HERE (not by React state), so both invariants are
 * unit-testable without rendering. `onBusyChange` mirrors it into the UI.
 */

import type { AreaFailure } from '@/lib/areaApi';
import type { AreaIdentityStartFlowResult } from '@/lib/areaIdentityStartFlow';

/** Clean, non-technical copy for a start failure. Never leaks provider detail. */
export function startFailureMessage(failure: AreaFailure): string {
  switch (failure.kind) {
    case 'PROVIDER_UNAVAILABLE':
      return 'Verifica identità temporaneamente non disponibile. Riprova più tardi.';
    case 'RATE_LIMITED':
      return 'Troppe richieste, attendi qualche istante e riprova.';
    case 'NOT_PREAUTHORIZED':
      return 'La verifica non è ancora disponibile per questa richiesta.';
    case 'EMAIL_NOT_VERIFIED':
      return 'Verifica prima il tuo indirizzo email.';
    case 'AUTH_INVALID':
      return 'Sessione scaduta: accedi di nuovo.';
    case 'NETWORK':
      return 'Problema di connessione. Riprova.';
    default:
      return 'Si è verificato un errore. Riprova.';
  }
}

/** Pure: the error message to surface for a flow outcome (null = no error). */
export function messageForStartOutcome(
  outcome: AreaIdentityStartFlowResult,
): string | null {
  switch (outcome.kind) {
    case 'REDIRECT':
    case 'NO_REDIRECT':
      return null;
    case 'NOT_PREAUTHORIZED':
      return 'La verifica non è ancora disponibile per questa richiesta.';
    case 'EMAIL_NOT_VERIFIED':
      return 'Verifica prima il tuo indirizzo email.';
    case 'NO_SESSION':
      return 'Sessione scaduta: accedi di nuovo.';
    case 'ME_UNAVAILABLE':
      return 'Impossibile leggere lo stato. Riprova.';
    case 'START_FAILED':
      return startFailureMessage(outcome.failure);
  }
}

export interface AreaIdentityStartControllerDeps {
  /** Run the pure start flow (reloadUser → token → /me → POST start). */
  performStart: () => Promise<AreaIdentityStartFlowResult>;
  /** Navigate the browser to the provider (window.location.assign). */
  navigate: (url: string) => void;
  /** Mirror the busy flag into the UI (button disabled). */
  onBusyChange: (busy: boolean) => void;
  /** Surface / clear the error message. */
  onError: (message: string | null) => void;
  /** Refresh the area read model (after a terminal/non-redirect outcome). */
  onRefresh: () => void;
}

export interface AreaIdentityStartController {
  start: (deps: AreaIdentityStartControllerDeps) => Promise<void>;
  isBusy: () => boolean;
}

/**
 * Lazily resolve a STABLE per-instance controller from a React ref. The
 * controller MUST survive re-renders (a fresh one would reset `busy` to false
 * and defeat the double-click guard), so it is created exactly once and kept on
 * `ref.current`. Mirrors the canonical `if (!ref.current) ref.current = …`
 * lazy-init — extracted here so the "create once / same instance" wiring is
 * unit-testable without rendering React.
 */
export function ensureStableController(ref: {
  current: AreaIdentityStartController | null;
}): AreaIdentityStartController {
  if (!ref.current) {
    ref.current = createAreaIdentityStartController();
  }
  return ref.current;
}

export function createAreaIdentityStartController(): AreaIdentityStartController {
  let busy = false;
  return {
    isBusy: () => busy,
    async start(deps: AreaIdentityStartControllerDeps): Promise<void> {
      if (busy) return; // double-click guard — a start is already in flight
      busy = true;
      deps.onBusyChange(true);
      deps.onError(null);
      // REDIRECT leaves the page: keep busy set so the button stays disabled and
      // a re-entrant start() is guarded until the browser actually navigates.
      let leavingPage = false;
      try {
        const outcome = await deps.performStart();
        if (outcome.kind === 'REDIRECT') {
          leavingPage = true;
          deps.navigate(outcome.url);
          return;
        }
        if (
          outcome.kind === 'NO_REDIRECT' ||
          outcome.kind === 'NOT_PREAUTHORIZED'
        ) {
          deps.onRefresh();
        }
        const message = messageForStartOutcome(outcome);
        if (message) deps.onError(message);
      } finally {
        if (!leavingPage) {
          busy = false;
          deps.onBusyChange(false);
        }
      }
    },
  };
}
